import React from "react";
import {Row, Seat, Zone} from "../../types/event";

/**
 * Draws the row letter beside each row, shared by the checkout seat picker
 * (features/event-details/SeatMapSection.tsx) and the read-only preview
 * (components/seatmap/SeatMapPreview.tsx) so the two cannot drift apart.
 *
 * `row_number_position` comes from the seat-map editor as "start" | "end" | "both";
 * "left" and "right" are the older spelling and are still honoured for layouts
 * uploaded before the editor changed. Anything unrecognised falls back to "both",
 * because a row whose letter silently vanishes is worse than one labelled twice.
 */

/** Clearance between the edge of the outermost seat and the letter. */
const LABEL_GAP = 21;

type Point = { x: number; y: number };

const seatOffset = (seat: Seat) => (seat.radius ?? 12) + LABEL_GAP;

/**
 * Unit vector along the row, first seat to last. Labels are pushed out along this
 * axis rather than along x, so a right-to-left or angled row puts its letter beyond
 * the row instead of on top of its own seats. A single-seat row has no axis of its
 * own, so it falls back to horizontal.
 */
function rowAxis(first: Seat, last: Seat): Point {
    const dx = (last.position?.x ?? 0) - (first.position?.x ?? 0);
    const dy = (last.position?.y ?? 0) - (first.position?.y ?? 0);
    const length = Math.hypot(dx, dy);
    if (!length) return {x: 1, y: 0};
    return {x: dx / length, y: dy / length};
}

function labelPoints(row: Row, originX: number, originY: number): Point[] {
    const seats = row.seats ?? [];
    const first = seats[0];
    const last = seats[seats.length - 1];
    // A row carrying no seats is an editor artefact, not a row anybody sits in.
    if (!first || !last) return [];

    const axis = rowAxis(first, last);

    const startPoint = {
        x: originX + (first.position?.x ?? 0) - axis.x * seatOffset(first),
        y: originY + (first.position?.y ?? 0) - axis.y * seatOffset(first),
    };
    const endPoint = {
        x: originX + (last.position?.x ?? 0) + axis.x * seatOffset(last),
        y: originY + (last.position?.y ?? 0) + axis.y * seatOffset(last),
    };

    switch (row.row_number_position) {
        case "start":
            return [startPoint];
        case "end":
            return [endPoint];
        // The older spelling is purely horizontal: it means the left or right edge of
        // the row on screen, whichever seat happens to sit there.
        case "left": {
            const leftmost = seats.reduce((a, b) => ((b.position?.x ?? 0) < (a.position?.x ?? 0) ? b : a));
            return [{
                x: originX + (leftmost.position?.x ?? 0) - seatOffset(leftmost),
                y: originY + (leftmost.position?.y ?? 0),
            }];
        }
        case "right": {
            const rightmost = seats.reduce((a, b) => ((b.position?.x ?? 0) > (a.position?.x ?? 0) ? b : a));
            return [{
                x: originX + (rightmost.position?.x ?? 0) + seatOffset(rightmost),
                y: originY + (rightmost.position?.y ?? 0),
            }];
        }
        default:
            return [startPoint, endPoint];
    }
}

export default function RowLabels({zones}: { zones: Zone[] }) {
    return (
        <>
            {zones.flatMap((zone) =>
                (zone.rows || []).flatMap((row) => {
                    const originX = (zone.position?.x ?? 0) + (row.position?.x ?? 0);
                    const originY = (zone.position?.y ?? 0) + (row.position?.y ?? 0);

                    return labelPoints(row, originX, originY).map((point, idx) => (
                        <text
                            key={`row-label-${row.uuid}-${idx}`}
                            x={point.x}
                            y={point.y}
                            fontSize={20}
                            fill="#4B5563"
                            textAnchor="middle"
                            dominantBaseline="middle"
                            pointerEvents="none"
                            style={{userSelect: "none"}}
                        >
                            {row.row_number}
                        </text>
                    ));
                })
            )}
        </>
    );
}
